import React from "react";
import {
	alpha,
	Box,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import PersonTwoToneIcon from "@mui/icons-material/PersonTwoTone";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import PhoneTwoToneIcon from "@mui/icons-material/PhoneTwoTone";
import MeetingRoomTwoToneIcon from "@mui/icons-material/MeetingRoomTwoTone";
import AccessTimeTwoToneIcon from "@mui/icons-material/AccessTimeTwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import moment from "moment";
import type { IScanVisitor } from "../../type/offlineExhibitorType";
import ExportScannedVisitorsExcel from "./ExportScannedVisitorsExcel";

interface Props {
	visitors?: IScanVisitor[];
	exhibitorName?: string;
}

const ExhibitorScannedVisitorsTab: React.FC<Props> = ({
	visitors,
	exhibitorName,
}) => {
	return (
		<Box>
			{/* Top Action Header Bar */}
			<Box
				sx={{
					p: 2,
					px: 2.5,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1.5,
					borderBottom: "1px solid",
					borderColor: "divider",
					bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="h6" fontWeight={700}>
						Scanned Visitors
					</Typography>
					<Chip
						label={`${visitors?.length || 0} Total`}
						size="small"
						color={visitors && visitors.length > 0 ? "primary" : "default"}
						sx={{ fontWeight: 700, borderRadius: 1 }}
					/>
				</Box>

				<ExportScannedVisitorsExcel
					visitors={visitors}
					exhibitorName={exhibitorName}
				/>
			</Box>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow
							sx={{
								bgcolor: (theme) =>
									alpha(theme.palette.primary.main, 0.04),
							}}
						>
							<TableCell
								sx={{ fontWeight: 700, width: 70, color: "text.primary" }}
							>
								SL
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Visitor No
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Visitor Name
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Visitor Email
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Visitor Phone
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Stall No
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Hall Name
							</TableCell>
							<TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
								Scan Time
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{visitors && visitors.length > 0 ? (
							visitors.map((visitor, idx) => (
								<TableRow
									key={visitor.id || idx}
									hover
									sx={{
										"&:hover": {
											bgcolor: (theme) =>
												alpha(theme.palette.primary.main, 0.02),
										},
									}}
								>
									<TableCell
										sx={{
											color: "text.secondary",
											fontWeight: 600,
										}}
									>
										{idx + 1}
									</TableCell>
									<TableCell>
										<Chip
											label={visitor.visitor_no || "N/A"}
											size="small"
											color="primary"
											variant="filled"
											sx={{
												fontWeight: 700,
												borderRadius: 1.5,
												fontSize: "0.75rem",
											}}
										/>
									</TableCell>
									<TableCell>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											<PersonTwoToneIcon
												fontSize="small"
												color="primary"
											/>
											<Typography
												variant="body2"
												fontWeight={700}
												color="text.primary"
											>
												{visitor.visitor_name || "N/A"}
											</Typography>
										</Box>
									</TableCell>
									<TableCell>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.8,
											}}
										>
											<EmailTwoToneIcon
												sx={{
													fontSize: 16,
													color: "text.secondary",
												}}
											/>
											<Typography
												variant="body2"
												color="text.primary"
											>
												{visitor.visitor_email || "-"}
											</Typography>
										</Box>
									</TableCell>
									<TableCell>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.8,
											}}
										>
											<PhoneTwoToneIcon
												sx={{
													fontSize: 16,
													color: "text.secondary",
												}}
											/>
											<Typography
												variant="body2"
												color="text.primary"
											>
												{visitor.visitor_phone || "-"}
											</Typography>
										</Box>
									</TableCell>
									<TableCell>
										<Chip
											label={visitor.stall_no || "N/A"}
											size="small"
											color="secondary"
											variant="outlined"
											sx={{
												fontWeight: "bold",
												borderRadius: 1.5,
											}}
										/>
									</TableCell>
									<TableCell>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.8,
											}}
										>
											<MeetingRoomTwoToneIcon
												fontSize="small"
												color="action"
											/>
											<Typography
												variant="body2"
												fontWeight={600}
												color="text.primary"
											>
												{visitor.hall_name || "-"}
											</Typography>
										</Box>
									</TableCell>
									<TableCell>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.8,
											}}
										>
											<AccessTimeTwoToneIcon
												sx={{
													fontSize: 16,
													color: "text.secondary",
												}}
											/>
											<Typography
												variant="body2"
												color="text.secondary"
											>
												{visitor.scan_time
													? moment(visitor.scan_time).format("LLL")
													: "-"}
											</Typography>
										</Box>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={8} align="center" sx={{ py: 6 }}>
									<QrCodeScannerTwoToneIcon
										sx={{
											fontSize: 48,
											color: "text.disabled",
											mb: 1,
										}}
									/>
									<Typography
										variant="h5"
										color="text.secondary"
										fontWeight={600}
									>
										No Visitor Scans Found
									</Typography>
									<Typography variant="body2" color="text.disabled">
										No visitors have scanned this exhibitor's stalls yet.
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

export default ExhibitorScannedVisitorsTab;
